import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * External dependencies
 */
import { omit, without, mapValues, isObject } from 'lodash';
import memize from 'memize';
/**
 * Internal dependencies
 */

import createReduxStore from './redux-store';
import createCoreDataStore from './store';
/** @typedef {import('./types').WPDataStore} WPDataStore */

/**
 * @typedef {Object} WPDataRegistry An isolated orchestrator of store registrations.
 *
 * @property {Function} registerGenericStore Given a namespace key and settings
 *                                           object, registers a new generic
 *                                           store.
 * @property {Function} registerStore        Given a namespace key and settings
 *                                           object, registers a new namespace
 *                                           store.
 * @property {Function} subscribe            Given a function callback, invokes
 *                                           the callback on any change to state
 *                                           within any registered store.
 * @property {Function} select               Given a namespace key, returns an
 *                                           object of the  store's registered
 *                                           selectors.
 * @property {Function} dispatch             Given a namespace key, returns an
 *                                           object of the store's registered
 *                                           action dispatchers.
 */

/**
 * @typedef {Object} WPDataPlugin An object of registry function overrides.
 *
 * @property {Function} registerStore registers store.
 */

/**
 * Creates a new store registry, given an optional object of initial store
 * configurations.
 *
 * @param {Object}  storeConfigs Initial store configurations.
 * @param {Object?} parent       Parent registry.
 *
 * @return {WPDataRegistry} Data registry.
 */

export function createRegistry() {
  var storeConfigs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var stores = {};
  var listeners = [];

  var __experimentalListeningStores = new Set();
  /**
   * Global listener called for each store's update.
   */


  function globalListener() {
    listeners.forEach(function (listener) {
      return listener();
    });
  }
  /**
   * Subscribe to changes to any data.
   *
   * @param {Function}   listener Listener function.
   *
   * @return {Function}           Unsubscribe function.
   */


  var subscribe = function subscribe(listener) {
    listeners.push(listener);
    return function () {
      listeners = without(listeners, listener);
    };
  };
  /**
   * Calls a selector given the current state and extra arguments.
   *
   * @param {string|WPDataStore} storeNameOrDefinition Unique namespace identifier for the store
   *                                                   or the store definition.
   *
   * @return {*} The selector's returned value.
   */


  function select(storeNameOrDefinition) {
    var storeName = isObject(storeNameOrDefinition) ? storeNameOrDefinition.name : storeNameOrDefinition;

    __experimentalListeningStores.add(storeName);

    var store = stores[storeName];

    if (store) {
      return store.getSelectors();
    }

    return parent && parent.select(storeName);
  }

  function __experimentalMarkListeningStores(callback, ref) {
    __experimentalListeningStores.clear();

    var result = callback.call(this);
    ref.current = Array.from(__experimentalListeningStores);
    return result;
  }

  var getResolveSelectors = memize(function (selectors) {
    return mapValues(omit(selectors, ['getIsResolving', 'hasStartedResolution', 'hasFinishedResolution', 'isResolving', 'getCachedResolvers']), function (selector, selectorName) {
      return function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        return new Promise(function (resolve) {
          var hasFinished = function hasFinished() {
            return selectors.hasFinishedResolution(selectorName, args);
          };

          var getResult = function getResult() {
            return selector.apply(null, args);
          }; // trigger the selector (to trigger the resolver)


          var result = getResult();

          if (hasFinished()) {
            return resolve(result);
          }

          var unsubscribe = subscribe(function () {
            if (hasFinished()) {
              unsubscribe();
              resolve(getResult());
            }
          });
        });
      };
    });
  }, {
    maxSize: 1
  });
  /**
   * Given the name of a registered store, returns an object containing the store's
   * selectors pre-bound to state so that you only need to supply additional arguments,
   * and modified so that they return promises that resolve to their eventual values,
   * after any resolvers have ran.
   *
   * @param {string|WPDataStore} storeNameOrDefinition Unique namespace identifier for the store
   *                                                   or the store definition.
   *
   * @return {Object} Each key of the object matches the name of a selector.
   */

  function __experimentalResolveSelect(storeNameOrDefinition) {
    return getResolveSelectors(select(storeNameOrDefinition));
  }
  /**
   * Returns the available actions for a part of the state.
   *
   * @param {string|WPDataStore} storeNameOrDefinition Unique namespace identifier for the store
   *                                                   or the store definition.
   *
   * @return {*} The action's returned value.
   */


  function dispatch(storeNameOrDefinition) {
    var storeName = isObject(storeNameOrDefinition) ? storeNameOrDefinition.name : storeNameOrDefinition;
    var store = stores[storeName];

    if (store) {
      return store.getActions();
    }

    return parent && parent.dispatch(storeName);
  } //
  // Deprecated
  // TODO: Remove this after `use()` is removed.
  //


  function withPlugins(attributes) {
    return mapValues(attributes, function (attribute, key) {
      if (typeof attribute !== 'function') {
        return attribute;
      }

      return function () {
        return registry[key].apply(null, arguments);
      };
    });
  }
  /**
   * Registers a generic store.
   *
   * @param {string} key    Store registry key.
   * @param {Object} config Configuration (getSelectors, getActions, subscribe).
   */


  function registerGenericStore(key, config) {
    if (typeof config.getSelectors !== 'function') {
      throw new TypeError('config.getSelectors must be a function');
    }

    if (typeof config.getActions !== 'function') {
      throw new TypeError('config.getActions must be a function');
    }

    if (typeof config.subscribe !== 'function') {
      throw new TypeError('config.subscribe must be a function');
    }

    stores[key] = config;
    config.subscribe(globalListener);
  }
  /**
   * Registers a new store definition.
   *
   * @param {WPDataStore} store Store definition.
   */


  function register(store) {
    registerGenericStore(store.name, store.instantiate(registry));
  }
  /**
   * Subscribe handler to a store.
   *
   * @param {string[]} storeName The store name.
   * @param {Function} handler   The function subscribed to the store.
   * @return {Function} A function to unsubscribe the handler.
   */


  function __experimentalSubscribeStore(storeName, handler) {
    if (storeName in stores) {
      return stores[storeName].subscribe(handler);
    } // Trying to access a store that hasn't been registered,
    // this is a pattern rarely used but seen in some places.
    // We fallback to regular `subscribe` here for backward-compatibility for now.
    // See https://github.com/WordPress/gutenberg/pull/27466 for more info.


    if (!parent) {
      return subscribe(handler);
    }

    return parent.__experimentalSubscribeStore(storeName, handler);
  }

  var registry = {
    registerGenericStore: registerGenericStore,
    stores: stores,
    namespaces: stores,
    // TODO: Deprecate/remove this.
    subscribe: subscribe,
    select: select,
    __experimentalResolveSelect: __experimentalResolveSelect,
    dispatch: dispatch,
    use: use,
    register: register,
    __experimentalMarkListeningStores: __experimentalMarkListeningStores,
    __experimentalSubscribeStore: __experimentalSubscribeStore
  };
  /**
   * Registers a standard `@wordpress/data` store.
   *
   * @param {string} storeName  Unique namespace identifier.
   * @param {Object} options    Store description (reducer, actions, selectors, resolvers).
   *
   * @return {Object} Registered store object.
   */

  registry.registerStore = function (storeName, options) {
    if (!options.reducer) {
      throw new TypeError('Must specify store reducer');
    }

    var store = createReduxStore(storeName, options).instantiate(registry);
    registerGenericStore(storeName, store);
    return store.store;
  }; //
  // TODO:
  // This function will be deprecated as soon as it is no longer internally referenced.
  //


  function use(plugin, options) {
    registry = _objectSpread(_objectSpread({}, registry), plugin(registry, options));
    return registry;
  }

  registerGenericStore('core/data', createCoreDataStore(registry));
  Object.entries(storeConfigs).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        name = _ref2[0],
        config = _ref2[1];

    return registry.registerStore(name, config);
  });

  if (parent) {
    parent.subscribe(globalListener);
  }

  return withPlugins(registry);
}
//# sourceMappingURL=registry.js.map